import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import BookingApi from "../../API/Apis/BookingApi";
import { AppStandardButton } from "../../components/UI/AppButtons";
import AppCards from "../../components/UI/AppCards";
import { requestParentResize } from "../../helpers/iframeSource";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import { t } from "i18next";
import owasp from "owasp-password-strength-test";

export default function UpdatePassword() {
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const isDesktopScreen = window.innerWidth >= 1310;
  const onlineUserId = localStorage.getItem("userID");
  const idSupplier = localStorage.getItem("idSupplier");
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [shouldNavigate, setShouldNavigate] = useState(false);
  useEffect(() => {
    requestParentResize(isDesktopScreen, 450, 760);
  }, []);
  owasp.config({
    allowPassphrases: false,
    minLength: 8,
    minOptionalTestsToPass: 4,
  });

  const validationSchema = Yup.object().shape({
    verificationCode: Yup.string().required(
      translate("Verification code is required")
    ),
    email: Yup.string()
      .email(translate("Invalid email"))
      .required(translate("Email is required")),

    newPassword: Yup.string()
      .required(translate("New Password is required"))
      .min(8, translate("Password must be at least 8 characters"))
      .matches(/[A-Z]/, translate("Must contain at least one uppercase letter"))
      .matches(/[a-z]/, translate("Must contain at least one lowercase letter"))
      .matches(/[0-9]/, translate("Must contain at least one digit"))
      .matches(
        /[^a-zA-Z0-9]/,
        translate("Must contain at least one special character")
      )
      .test(
        "password-strength",
        translate("Password is too weak. Please choose a stronger one."),
        function (value) {
          if (!value) return false;
          const result = owasp.test(value);
          if (!result.strong) {
            return this.createError({
              message: result.errors.join(" "),
            });
          }
          return true;
        }
      ),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(validationSchema) });

  const handleCloseModal = () => {
    setOpenModal(false);
    if (shouldNavigate) navigate("/signin");
  };

  const handleRegistration = async (data) => {
    try {
      const response = await BookingApi.forgotPassword(
        data,
        onlineUserId,
        idSupplier
      );
      setModalMessage(
        response.status === "success"
          ? "Password Updated successfully"
          : response.errorMessage
      );
      setShouldNavigate(response.status === "success");
      setOpenModal(true);
    } catch (error) {
      setModalMessage("An error occurred. Please try again.");
      setOpenModal(true);
    }
  };

  return (
    <div
      className="flex flex-col items-center min-h-[60vh] justify-center"
      style={{ paddingTop: isDesktopScreen ? "20px" : "36px" }}
    >
      <form
        className="w-full md:w-1/2"
        onSubmit={handleSubmit(handleRegistration)}
      >
        <AppCards className="box-border p-4 flex justify-center">
          <div className="w-full max-w-md">
            {" "}
            <header className="flex flex-col gap-2 box-border">
              <h2
                className="w-full text-xl font-bold text-white text-center"
                style={{ fontFamily: "MBCorpoAScreen1Cond-Regular-Web" }}
              >
                {translate("Change Password")}
              </h2>
            </header>
            <p
              className="text-white text-center"
              style={{ fontFamily: "MBCorpoAScreen1Cond-Regular-Web" }}
            >
              {translate("You can change your password from here")}
            </p>
            <div className="flex flex-col items-center gap-4">
              {renderInput("verificationCode", "Verification Code", "text")}
              {renderInput("email", "Email Address", "email")}
              {renderInput("newPassword", "New Password", "password")}
              {/* <Button
                type="submit"
                fullWidth
                variant="contained"
                className="mt-2 mb-2"
                sx={{
                  bgcolor: "#0078D6",
                  color: "#FFFFFF",
                  fontWeight: "bold",
                  width: isDesktopScreen ? "421px" : "360px",
                }}
              >
                {translate("Sign in")}
              </Button> */}
              <div
                className="w-full"
                style={{ marginBottom: isDesktopScreen ? "0px" : "80%" }}
              >
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 2,
                    mb: 2,
                    bgcolor: "#0078D6",
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    "&:hover": {
                      background: "#0078D6",
                    },
                    width: !isDesktopScreen ? "360px" : "421px", // Ensuring button width matches input
                    fontFamily: "MBCorpoSTitle-CondRegular-Web-new",
                  }}
                >
                  {translate("Sign in")}
                </Button>
              </div>
            </div>
          </div>
        </AppCards>
      </form>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{ style: { borderRadius: "20px" } }}
      >
        <DialogTitle style={{ padding: "3rem" }}>{modalMessage}</DialogTitle>
        <DialogActions style={{ justifyContent: "center" }}>
          <AppStandardButton variant="outlined" onClick={handleCloseModal}>
            <p className="text-black normal-case">{t("Close")}</p>
          </AppStandardButton>
        </DialogActions>
      </Dialog>
    </div>
  );

  function renderInput(name, label, type) {
    return (
      <Grid item xs={12} sm={6} className="w-full">
        <label
          style={{
            color: "#FFFFFF",
            fontFamily: "MBCorpoAScreen1Cond-Regular-Web",
          }}
        >
          {t(label)}
        </label>
        <TextField
          required
          fullWidth
          {...register(name)}
          error={!!errors[name]}
          helperText={errors[name]?.message}
          label={translate(label)}
          type={type}
          className="w-full"
          style={{
            backgroundColor: "#D9D9D9",
            borderRadius: "8px",
            color: "black",
            marginTop: "4px",
            marginBottom: "10px",
            width: isDesktopScreen ? "421px" : "360px",
          }}
          InputProps={{
            className: "MBCorpoSText-Regular-Web-new",
            sx: {
              fontFamily: "MBCorpoSText-Regular-Web-new",
              height: "38px",
            },
          }}
          InputLabelProps={{
            className: "MBCorpoAScreen1Cond-Regular-Web",
            sx: {
              "&.MuiInputLabel-root": {
                fontFamily: "MBCorpoAScreen1Cond-Regular-Web",
                fontSize: "16px",
                marginTop: "-6px",
              },
              "&.MuiInputLabel-shrink": {
                display: "none",
              },
            },
          }}
          id={name}
        />
      </Grid>
    );
  }
}
